/**
 * `<NotesList>` — flat list of `<NoteRow>` rows + "Load more" button
 * (Batch 30j-6).
 *
 * "Load more" is the binding pagination strategy (HoD §7) — explicit
 * click rather than auto-infinite-scroll. Reasons in HoD §7: clean
 * interaction with render-and-hide canon (no IntersectionObserver edge
 * cases), notes are scarce not abundant, cursor advance is
 * explicit/debuggable, scroll-restoration is automatic.
 *
 * The "Notes (N)" count reflects rendered count, not server total —
 * backend doesn't ship a total-count header; surfacing one is BACKLOG.
 */
import type { JSX } from "react";

import { Button } from "@/components/common/Button";
import { NoteRow } from "./NoteRow";
import type { NoteOut } from "@/hooks/queries/notes/useNotes";

interface NotesListProps {
  rows: NoteOut[];
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
}

export function NotesList({
  rows,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
}: NotesListProps): JSX.Element {
  return (
    <section data-testid="notes-list">
      <h3 className="mb-3 text-2xs font-bold uppercase tracking-[0.12em] text-text-tertiary">
        Notes ({rows.length})
      </h3>
      <div className="flex flex-col gap-3">
        {rows.map((row) => (
          <NoteRow key={row.id} note={row} />
        ))}
      </div>
      {hasNextPage ? (
        <div className="mt-4 flex justify-center">
          <Button
            type="button"
            variant="ghost"
            onClick={onLoadMore}
            disabled={isFetchingNextPage}
            data-testid="notes-load-more"
          >
            {isFetchingNextPage ? "Loading…" : "Load 25 more"}
          </Button>
        </div>
      ) : null}
    </section>
  );
}
